import {extend, override} from 'flarum/extend';
import DiscussionComposer from 'flarum/components/DiscussionComposer';
import EditPostComposer from 'flarum/components/EditPostComposer';
import ReplyComposer from 'flarum/components/ReplyComposer';
import Button from "flarum/components/Button";

import Stream from "flarum/utils/Stream";


export default function () {
  // DiscussionComposer
  /**
   * Set lock button to false as default.
   */
  extend(DiscussionComposer.prototype, 'oninit', function () {
    this.isLocked = Stream(false);
  });

  extend(DiscussionComposer.prototype, 'headerItems', function (items) {
    addLockDiscussionButton(this, items);
  });

  /**
   * Create new discussion first and then lock the new discussion.
   */
  override(DiscussionComposer.prototype, 'onsubmit', function () {
    this.loading = true;

    const data = this.data();

    app.store
      .createRecord('discussions')
      .save(data)
      .then((discussion) => {
        if (discussion.canLock()) {
          discussion.save(getData(this))
            .then(() => {
              updateDiscussionComposer(discussion);
            }, this.loaded.bind(this));
        } else {
          updateDiscussionComposer(discussion);
        }
      }, this.loaded.bind(this));
  });

  /**
   * Hide composer, refresh discussions and go to new discussion.
   * @param discussion
   */
  function updateDiscussionComposer(discussion) {
    app.composer.hide();
    app.discussions.refresh();
    m.route.set(app.route.discussion(discussion));
  }

  // EditPostComposer
  /**
   * Set lock button the the lock state of the discussion.
   */
  extend(EditPostComposer.prototype, 'oninit', function () {
    this.isLocked = Stream(this.attrs.post.discussion().isLocked());
  });

  extend(EditPostComposer.prototype, 'data', function (data) {
    if (this.attrs.post.discussion().canLock()) {
      data.isLocked = this.isLocked();
    }
  });

  extend(EditPostComposer.prototype, 'onsubmit', function () {
    if (this.attrs.post.discussion().canLock()) {
      this.attrs.post.discussion().save(getData(this))
        .then(() => app.composer.hide(), this.loaded.bind(this));
    }
  });

  extend(EditPostComposer.prototype, 'headerItems', function (items) {
    addLockDiscussionButton(this, items);
  });

  override(EditPostComposer.prototype, 'focus', function () {
    extendFocus(this);
  });

  // ReplyComposer
  /**
   * Set lock button the the lock state of the discussion.
   */
  extend(ReplyComposer.prototype, 'oninit', function () {
    this.isLocked = Stream(this.attrs.discussion.isLocked());
  });

  extend(ReplyComposer.prototype, 'data', function (data) {
    if (this.attrs.discussion.canLock()) {
      data.isLocked = this.isLocked();
    }
  });

  extend(ReplyComposer.prototype, 'headerItems', function (items) {
    addLockDiscussionButton(this, items);
  });

  /**
   * Override onsubmit in order to lock discussion after comment submission.
   */
  override(ReplyComposer.prototype, 'onsubmit', function () {
    const discussion = this.attrs.discussion;

    this.loading = true;
    m.redraw();

    const data = this.data();

    app.store
      .createRecord('posts')
      .save(data)
      .then((post) => {
        if (this.attrs.discussion.canLock()) {
          this.attrs.discussion.save(getData(this))
            .then(() => app.composer.hide(), this.loaded.bind(this));
        }
        // If we're currently viewing the discussion which this reply was made
        // in, then we can update the post stream and scroll to the post.
        if (app.viewingDiscussion(discussion)) {
          const stream = app.current.get('stream');
          stream.update().then(() => stream.goToNumber(post.number()));
        } else {
          // Otherwise, we'll create an alert message to inform the user that
          // their reply has been posted, containing a button which will
          // transition to their new post when clicked.
          const viewButton = Button.component({
            className: 'Button Button--link',
            onclick: () => {
              m.route.set(app.route.post(post));
              app.alerts.dismiss(alert);
            },
          }, app.translator.trans('core.forum.composer_reply.view_button'));
          app.alerts.show(
            { type: 'success',
              controls: [viewButton] },
              app.translator.trans('core.forum.composer_reply.posted_message')
          );
        }

        app.composer.hide();
      }, this.loaded.bind(this));
  });

  override(ReplyComposer.prototype, 'focus', function () {
    extendFocus(this);
  });

  // Common
  function extendFocus(composer) {
    composer.$("textarea:enabled:visible").focus();
  }

  function getData(composer) {
    return {isLocked: composer.isLocked()};
  }

  function addLockDiscussionButton(composer, items) {
    if (app.session.user.canEdit()) {
      const lockNameIcon = (composer.isLocked()) ? 'lock' : 'lock-open';
      const lockNameTitle = (composer.isLocked()) ? 'lock' : 'lock_open';

      items.add(
        'lock',
        Button.component({
          icon: `fas fa-${lockNameIcon}`,
          className: 'Button lock-discussion-button',
          onclick: () => composer.isLocked(!composer.isLocked()),
          title: app.translator.trans(`core.forum.text_editor.${lockNameTitle}`),
        }, app.translator.trans(`core.forum.text_editor.${lockNameTitle}`)),
        0
      );
    }
  }

}
